import styled from "@emotion/styled";
import { TextField } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { useCallback, useRef, type CSSProperties } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { Icon } from "../../icons";
import { transientOptions } from "../../utilities";
import { useFieldContext } from "../FormFieldContext";
import type { FieldTextProps } from "./FieldText";
import { useFieldLabelAndDescription, useFieldPlaceholder, useHandleBlur } from "./helpers";

const Container = styled.div`
  width: 100%;

  > div {
    width: 100%;
    background-color: transparent;
  }
`;

const SearchIcon = styled(Icon)`
  padding: 0 8px 0 0;

  color: ${({ theme }) => theme.generalTextIconVariant};
`;

const ClearIcon = styled(Icon, transientOptions)<{ $show: boolean }>`
  color: ${({ theme }) => theme.generalTextIconVariant};

  padding: 0 4px 0 0;

  opacity: ${({ $show }) => ($show ? 0.7 : 0)};

  pointer-events: ${({ $show }) => ($show ? "auto" : "none")};

  cursor: pointer;

  transition: opacity 150ms ease-in-out;
`;

const Search = styled(TextField, transientOptions)<{ $borderRadius: CSSProperties["borderRadius"] }>`
  height: 100%;

  font-size: 16px;

  fieldset {
    top: 0;
  }

  fieldset > legend {
    display: none;
  }

  .MuiInputLabel-root {
    display: none;
  }

  .MuiInputBase-root {
    height: 100%;

    color: ${({ theme }) => theme.generalTextIconVariant};
    background-color: ${({ theme }) => theme.surface};

    font-weight: 500;

    border-radius: ${({ $borderRadius, theme }) => `${$borderRadius ?? `${theme.borderRadius}px`}`};
  }
`;

type Props = FieldTextProps & Pick<CSSProperties, "borderRadius">;

export const FieldSearch = ({ borderRadius, ...props }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const inputRef = useRef<HTMLInputElement>(null);

  const { controlId, name } = useFieldContext();

  const providedPlaceholder = useFieldPlaceholder(name);

  const placeholder = providedPlaceholder ? providedPlaceholder : (props.placeholder ?? formatMessage("search"));

  const [label, helperText] = useFieldLabelAndDescription(name);

  const { submitForm } = useFormikContext();
  const [{ value, onChange }, _, { setValue }] = useField<string>(name);

  const handleBlur = useHandleBlur();

  const handleClear = useCallback(() => {
    void setValue("");
    void submitForm();
    inputRef.current?.focus();
  }, [setValue, submitForm]);

  return (
    <Container>
      <Search
        {...props}
        id={controlId}
        name={name}
        variant="outlined"
        placeholder={placeholder}
        value={value}
        label={label ?? props.label}
        helperText={props.helperText ?? helperText}
        inputRef={inputRef}
        onBlur={handleBlur}
        onChange={onChange}
        slotProps={{
          input: {
            placeholder,
            startAdornment: <SearchIcon icon="search" size={16} />,
            endAdornment: <ClearIcon $show={!!value} icon="delete-left" size={16} onClick={handleClear} />,
          },
        }}
        autoComplete="off"
        $borderRadius={borderRadius}
      />
    </Container>
  );
};

const formatStrings = {
  search: {
    en: "Search",
  },
} satisfies FormatMessages;
